import { z } from 'zod'
import {
  dollarPriceSaveSections,
  venueMarketingSections,
  notBothStayKinds,
  sectionOrderSaveSchema,
} from '../dto/venue-marketing.dto.js'

/**
 * A marketing document on its way in.
 *
 * The same sections the API serves, with one difference: **this one is strict at
 * the top level.** Reading has to tolerate a section it has not heard of, because
 * a response schema that refuses one breaks every older browser tab the moment
 * the API grows. Writing has the opposite duty — a document naming `contacts`
 * where it meant `contact` would save cleanly, serve cleanly, and render a page
 * with no phone number on it.
 *
 * Strict at the top level and not below it. The section names are what a hand
 * written or imported document gets wrong, and they are what the consuming app
 * reads by. A misspelled field *inside* a section is stripped rather than
 * refused, which the editor makes unreachable anyway — a form writes its own
 * keys.
 */
export const saveVenueMarketingContentSchema = venueMarketingSections
  // `sectionOrder` is the one field where the read schema is deliberately more
  // forgiving than this one: reading drops a name it does not recognise so a
  // retired section cannot take a whole page down, while saving refuses it.
  // The prices printed after the site's own `$` are the other: read as stored,
  // saved only as a number.
  .extend({ sectionOrder: sectionOrderSaveSchema.optional(), ...dollarPriceSaveSections })
  .strict()
  .refine(notBothStayKinds.check, { message: notBothStayKinds.message })

export type SaveVenueMarketingContent = z.infer<typeof saveVenueMarketingContentSchema>
